import { Component, Inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserDetail } from '../../admin.model';
import { AdminService } from '../../services/admin.service';
import { CustomersService } from '../../../customer/services/customers.service';
import { NotificationService } from 'shared/services/notification.service';
import { forkJoin } from 'rxjs';
import { debouncedSearch } from 'shared/utils/rx-operators';

export interface EditUserRolesDialogData {
  user: UserDetail;
  profileId: string;
  profileName: string;
  currentUserId: string;
  isEditingSelf: boolean;
  customerId?: string;
}

/** Static group catalog rendered as toggle buttons. Order = display order. */
interface GroupOption {
  code: string; // e.g. 'GROUP_Administrator'
  label: string;
  description: string;
}

const GROUP_OPTIONS: GroupOption[] = [
  {
    code: 'GROUP_Administrator',
    label: 'Administrator',
    description: 'Full platform admin (users, transactions, customers). Excludes ERP wiring.',
  },
  {
    code: 'GROUP_ERP_Administrator',
    label: 'ERP Administrator',
    description: 'Manage SFTP, ERP imports/exports, schedules, and the ERP dashboard.',
  },
  {
    code: 'GROUP_Accountant',
    label: 'Accountant',
    description: 'Financial operations and reporting.',
  },
  {
    code: 'GROUP_Clerk',
    label: 'Clerk',
    description: 'Basic transaction processing.',
  },
  {
    code: 'GROUP_Viewer',
    label: 'Viewer',
    description: 'Read-only access.',
  },
  {
    code: 'GROUP_Customer',
    label: 'Customer',
    description: 'Customer portal access for payments and invoices.',
  },
];

@Component({
  selector: 'app-edit-user-roles',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
  ],
  templateUrl: './edit-user-roles.component.html',
  styleUrls: ['./edit-user-roles.component.scss'],
})
export class EditUserRolesComponent implements OnInit {
  rolesControl = new FormControl<string[]>([]);
  searchControl = new FormControl('');

  groupOptions = GROUP_OPTIONS;
  /** Codes (`GROUP_*`) of every currently-toggled group. */
  selectedGroups = signal<Set<string>>(new Set());

  availableRoles = signal<string[]>([]);
  filteredRoles = signal<string[]>([]);
  currentUserRoles = signal<string[]>([]);
  isLoading = signal(true);
  isSaving = signal(false);

  /** Total selected = groups + individual rights, for the header counter. */
  selectedTotal = computed(() => this.selectedGroups().size + (this.rolesControl.value?.length || 0));

  constructor(
    public dialogRef: MatDialogRef<EditUserRolesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditUserRolesDialogData,
    private adminService: AdminService,
    private customersService: CustomersService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.loadRoles();

    this.searchControl.valueChanges
      .pipe(debouncedSearch())
      .subscribe((searchText) => {
        this.filterRoles(searchText || '');
      });
  }

  loadRoles(): void {
    this.isLoading.set(true);

    const isCustomerPortalUser = !!this.data.customerId;
    const userRolesObservable = isCustomerPortalUser
      ? this.customersService.getCustomerPortalUserRoles(this.data.customerId!, this.data.user.userNumber)
      : this.adminService.getUserProfileRoles(this.data.profileId, this.data.user.userNumber);

    forkJoin({
      grantableRoles: this.adminService.getCurrentUserRoles(this.data.profileId),
      userRoles: userRolesObservable,
    }).subscribe({
      next: (result) => {
        this.currentUserRoles.set(result.grantableRoles);

        // Individual rights are everything that isn't a GROUP_* role.
        // When editing self, only show the user's own non-group roles so
        // they can remove but never add new ones.
        if (this.data.isEditingSelf) {
          const individualRoles = result.userRoles.filter((role) => !role.startsWith('GROUP_'));
          this.availableRoles.set(individualRoles);
          this.filteredRoles.set(individualRoles);
        } else {
          const individualGrantable = result.grantableRoles.filter((role) => !role.startsWith('GROUP_'));
          this.availableRoles.set(individualGrantable);
          this.filteredRoles.set(individualGrantable);
        }

        // Existing assignment: collect ALL GROUP_* roles into the toggle
        // set; everything else becomes individual rights.
        const groups = new Set<string>();
        const individuals: string[] = [];
        for (const r of result.userRoles || []) {
          if (r.startsWith('GROUP_')) groups.add(r);
          else individuals.push(r);
        }
        this.selectedGroups.set(groups);
        this.rolesControl.setValue(individuals);

        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Failed to load roles:', error);
        this.notificationService.error('Failed to load roles: ' + (error?.message || 'Unknown error'));
        this.isLoading.set(false);
      },
    });
  }

  /** Toggle a single group on/off. */
  toggleGroup(code: string): void {
    if (this.data.isEditingSelf) return;
    const next = new Set(this.selectedGroups());
    if (next.has(code)) next.delete(code);
    else next.add(code);
    this.selectedGroups.set(next);
  }

  isGroupSelected(code: string): boolean {
    return this.selectedGroups().has(code);
  }

  filterRoles(searchText: string): void {
    const available = this.availableRoles();
    if (!searchText) {
      this.filteredRoles.set(available);
      return;
    }
    const searchLower = searchText.toLowerCase();
    this.filteredRoles.set(available.filter((role) => role.toLowerCase().includes(searchLower)));
  }

  selectAll(): void {
    this.rolesControl.setValue([...this.availableRoles()]);
  }

  deselectAll(): void {
    this.rolesControl.setValue([]);
  }

  removeRole(role: string): void {
    const currentRoles = this.rolesControl.value || [];
    this.rolesControl.setValue(currentRoles.filter((r) => r !== role));
  }

  onSave(): void {
    // Backend accepts a flat array of roles. GROUP_* entries are
    // expanded server-side; individual roles are checked per-role
    // against the caller's grantable set.
    const rolesToSave = [...this.selectedGroups(), ...(this.rolesControl.value || [])];

    this.isSaving.set(true);

    if (this.data.customerId) {
      this.customersService
        .setCustomerPortalUserRoles(this.data.customerId, this.data.user.userNumber, rolesToSave)
        .subscribe({
          next: () => {
            this.notificationService.success('User roles updated successfully');
            this.dialogRef.close(true);
          },
          error: (error) => {
            console.error('Save error:', error);
            this.notificationService.error('Failed to update roles: ' + error.message);
            this.isSaving.set(false);
          },
        });
    } else {
      this.adminService.updateUserProfileRoles(this.data.profileId, this.data.user.userNumber, rolesToSave).subscribe({
        next: () => {
          this.notificationService.success('User roles updated successfully');
          this.dialogRef.close(true);
        },
        error: (error) => {
          console.error('Save error:', error);
          this.notificationService.error('Failed to update roles: ' + error.message);
          this.isSaving.set(false);
        },
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  get individualSelectedCount(): number {
    return this.rolesControl.value?.length || 0;
  }

  get individualTotalCount(): number {
    return this.availableRoles().length;
  }
}
